/**
 * Field cache helper functions for React Query.
 * Service functions for cache operations outside of React components.
 */

import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import { processFieldResponse } from "common/utils/helpers";
import { Field, FieldResponse } from "model/fieldTypes";
import { invalidateFieldData } from "services/fieldOperations";

const fieldApi = new API<FieldResponse>("fields", "v2");

/**
 * Fetch multiple fields by their IDs and update React Query cache.
 * @param fieldIds - Array of field IDs to fetch
 * @param queryClient - React Query client instance
 * @returns Promise resolving to array of FieldResponse (raw API responses)
 */
export async function fetchFieldsByIdQuery(
  fieldIds: Array<string>,
  queryClient: QueryClient
): Promise<Array<FieldResponse>> {
  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }

  // Fetch all fields using the API
  const responses = await fieldApi.getMany(fieldIds);

  // Process the responses
  const processedFields = processFieldResponse(responses);

  // Update React Query cache for each field and handle validation
  processedFields.forEach((field) => {
    // Check if field was previously invalidated
    const cachedField = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(field.guid)
    );

    // If field has tiles and was invalidated, validate it
    if (
      cachedField?.invalidatedTimestamp &&
      field.tiles &&
      field.tiles.length > 0
    ) {
      const {
        invalidatedTimestamp: _invalidatedTimestamp,
        ...fieldWithoutInvalidated
      } = field;
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(field.guid),
        fieldWithoutInvalidated as Field
      );
    } else {
      // Update cache with latest data
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(field.guid),
        field
      );
    }
  });

  // Return raw responses to match standard behavior
  return responses;
}

/**
 * Hook wrapper for fetchFieldsByIdQuery that automatically provides QueryClient
 */
export function useFetchFieldsByIdQuery() {
  const queryClient = useQueryClient();
  return async (fieldIds: Array<string>) =>
    fetchFieldsByIdQuery(fieldIds, queryClient);
}

/**
 * Change field visibility in React Query cache.
 * @param update - Object with guid and visibility properties
 * @param queryClient - React Query client instance
 */
export function changeFieldVisibilityQuery(
  update: {
    guid: string;
    visibility: "none" | "visible";
  },
  queryClient: QueryClient
): void {
  // Get current cached field data
  const currentField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(update.guid)
  );

  if (currentField) {
    // Update visibility in cached field
    queryClient.setQueryData<Field>(queryKeys.fields.detail(update.guid), {
      ...currentField,
      visibility: update.visibility,
    });
  }
}

/**
 * Hook wrapper for changeFieldVisibilityQuery that automatically provides QueryClient
 */
export function useChangeFieldVisibilityQuery() {
  const queryClient = useQueryClient();
  return (update: { guid: string; visibility: "none" | "visible" }) =>
    changeFieldVisibilityQuery(update, queryClient);
}

/**
 * Invalidate field data in React Query cache (mark field as needing re-fetch).
 * Sets invalidatedTimestamp, clears tiles array, and triggers a refetch.
 * @param fieldGuid - The field guid to invalidate
 * @param queryClient - React Query client instance
 */
export function invalidateFieldDataQuery(
  fieldGuid: string,
  queryClient: QueryClient
): void {
  invalidateFieldData(fieldGuid, queryClient);
}

/**
 * Hook wrapper for invalidateFieldDataQuery that automatically provides QueryClient
 */
export function useInvalidateFieldDataQuery() {
  const queryClient = useQueryClient();
  return (fieldGuid: string) =>
    invalidateFieldDataQuery(fieldGuid, queryClient);
}

/**
 * Optimistically update fields for move-in operation.
 * Sets animalGroupNow on new field and clears it on current field.
 * @param newFieldGuid - The field GUID where animals are moving to
 * @param queryClient - React Query client instance
 * @param currentFieldGuid - The field GUID where animals are moving from (optional)
 */
export function optimisticFieldMoveIn(
  newFieldGuid: string,
  queryClient: QueryClient,
  currentFieldGuid?: string
): void {
  // Get new field from cache
  const newField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(newFieldGuid)
  );

  if (currentFieldGuid) {
    // Get current field from cache
    const currentField = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(currentFieldGuid)
    );

    // Validate that current and new fields have the same animal group
    if (
      newField &&
      currentField &&
      newField.animalGroup &&
      newField.animalGroup === currentField.animalGroup &&
      currentField.animalGroupNow === currentField.animalGroup
    ) {
      // Update new field: set animalGroupNow
      queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
        ...newField,
        animalGroupNow: newField.animalGroup,
      });

      // Update current field: clear animalGroupNow
      queryClient.setQueryData<Field>(
        queryKeys.fields.detail(currentFieldGuid),
        {
          ...currentField,
          animalGroupNow: undefined,
        }
      );
    }
  } else if (newField) {
    // No current field, just set animalGroupNow on new field
    queryClient.setQueryData<Field>(queryKeys.fields.detail(newFieldGuid), {
      ...newField,
      animalGroupNow: newField.animalGroup,
    });
  }
}

/**
 * Move animals into a field using React Query.
 * Optimistically updates fields and invalidates field queries for refetching.
 * @param currentFieldGuid - The field GUID where animals are moving from (optional)
 * @param newFieldGuid - The field GUID where animals are moving to
 * @param _dateTimeIn - The date/time when animals are moved in (kept for API compatibility)
 * @param queryClient - React Query client instance
 */
export async function moveIntoFieldQuery(
  currentFieldGuid: string | undefined,
  newFieldGuid: string,
  _dateTimeIn: Date,
  queryClient: QueryClient
): Promise<void> {
  // Optimistically update fields
  optimisticFieldMoveIn(newFieldGuid, queryClient, currentFieldGuid);

  // Invalidate field queries so they will be refetched after reading creation
  const fieldsToRefetch = [newFieldGuid];
  if (currentFieldGuid) {
    fieldsToRefetch.push(currentFieldGuid);
  }

  // Invalidate individual field queries
  await Promise.all(
    fieldsToRefetch.map((fieldGuid) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(fieldGuid),
      })
    )
  );

  // Also invalidate the fields list query to ensure consistency
  await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
}

/**
 * Validate field data in React Query cache (remove invalidatedTimestamp).
 * @param fieldGuid - The field guid to validate
 * @param queryClient - React Query client instance
 */
export function validateFieldDataQuery(
  fieldGuid: string,
  queryClient: QueryClient
): void {
  // Get current cached field data
  const currentField = queryClient.getQueryData<Field>(
    queryKeys.fields.detail(fieldGuid)
  );

  if (currentField) {
    // Remove invalidatedTimestamp from cached field
    const {
      invalidatedTimestamp: _invalidatedTimestamp,
      ...fieldWithoutInvalidated
    } = currentField;
    queryClient.setQueryData<Field>(
      queryKeys.fields.detail(fieldGuid),
      fieldWithoutInvalidated as Field
    );
  }
}

/**
 * Hook wrapper for validateFieldDataQuery that automatically provides QueryClient
 */
export function useValidateFieldDataQuery() {
  const queryClient = useQueryClient();
  return (fieldGuid: string) => validateFieldDataQuery(fieldGuid, queryClient);
}

/**
 * Reset all fields from React Query cache.
 * Removes all field-related queries from the cache.
 * @param queryClient - React Query client instance
 */
export function resetFieldsQueryCache(queryClient: QueryClient): void {
  // Remove all field-related queries from cache
  // With hierarchical keys, this removes all queries starting with ["fields"]
  queryClient.removeQueries({ queryKey: queryKeys.fields.all });
}

/**
 * Hook wrapper for resetFieldsQueryCache that automatically provides QueryClient
 */
export function useResetFieldsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetFieldsQueryCache(queryClient);
}
